import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import moment from 'moment';
import { publishQueries, publishUserQueries } from '@/entities/sets/publish';
import { ErrorPopup, Modal } from '@/shared/ui';
import useToggle from '@/shared/hooks/useVisible';
import { RecentPublishedOption } from './recent-published-option';
import { usePersistStore } from '@/shared/lib/store';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { PublishedCompletePopup } from './published-complete-popup';
import { showToast } from '@/shared/utils/toast';
import {
    PublishOptionHeader,
    PublishOptionPeriod,
    PublishOptionStep1,
    PublishOptionStep2Class,
} from '@/widgets/published-box';
import { publishedBoxApi } from '@/shared/api/sets/publish';
import { vivaClassEvalSave } from '@/shared/utils/viva-class';
import { sendTraceLog } from '@/shared/log/tracker';
import { moveToPodV2, setPodV2PublishGroupCache } from '@/shared/utils/pod-v2';
import { isVisibleClassStudent } from '@/shared/utils/publish';

export const PublishOptionPopup = ({
    checkList,
    onClose,
}: {
    checkList: DATA.Exam[];
    onClose: () => void;
}) => {
    const { sUserId, sDefaultUserId } = usePersistStore();
    const { showModal } = useModalStore();
    const { sMyClassMemberList } = usePersistStore();

    const [purpose, setPurpose] = useState<DATA.PurposeType>();
    const [selectClass, setSelectClass] = useState<
        API.MyClassMemberList.ClassData | { classInfo: { classSeq: number; clsName: string } }
    >();
    const [classMemberList, setClassMemberList] = useState<API.MyClassMemberList.ClassData[]>([]);

    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();
    const [hours, setHours] = useState(0); // 시간 초기값
    const [minutes, setMinutes] = useState(0); // 분 초기값
    const [periodWhether, setPeriodWhether] = useState(false); // 응시 기간 여부
    const [timerWhether, setTimerWhether] = useState(false); // 제한 시간 여부

    const [tags, setTags] = useState<DATA.TagItem[]>([]);

    const [recentPublishedVisible, setRecentPublishedVisible] = useToggle();

    const checkIdList = checkList?.map((v) => v.examId.toString());
    const checkNameList = checkList?.map((v) => v.name);
    const checkCurriBookList = checkList?.map((v) => v.curriBookCd.toString());

    const timestamp = Date.now();

    const isClass = purpose === 'class';
    const step1_disabled_true = !!purpose;

    const step2_disabled_true_class = isClass && step1_disabled_true && tags.length > 0;
    const step3_disabled_true_class =
        isClass &&
        step2_disabled_true_class &&
        ((!periodWhether && hours > 0) ||
            minutes > 0 ||
            (periodWhether &&
                !!startDate &&
                !!endDate &&
                ((timerWhether && (hours > 0 || minutes > 0)) || !timerWhether)));

    // 출력용
    const step2_disabled_true = !isClass && !!startDate && !!endDate;

    const publishBtnDisabled =
        !(step2_disabled_true || step3_disabled_true_class || purpose === 'output') ||
        (purpose === 'URL' && !!timerWhether && hours === 0 && minutes === 0);

    const registerStudent = publishUserQueries.postRegisterUser();
    const savePublish = publishQueries.publish.postPublishExam();

    useEffect(() => {
        if (sMyClassMemberList) {
            //(1:담임 2:전담 5:라이트)
            const filterList = sMyClassMemberList?.filter(
                (item) =>
                    (item.classInfo.directCourseYn === 'N' &&
                        ((item.classInfo.schTypeCode === 'ES' &&
                            item.classInfo.clsTypeCode === '1') ||
                            item.classInfo.clsTypeCode === '2')) ||
                    (item.classInfo.directCourseYn === 'Y' && item.classInfo.clsTypeCode === '5')
            );

            const newList = filterList?.map((item) => {
                const visibleMembers = item.memberInfo?.filter(isVisibleClassStudent) ?? [];
                return {
                    ...item,
                    checkStudents: visibleMembers.map((member) => ({
                        name: member.name,
                        memberId: member.orgMemberId,
                        checked: false,
                    })),
                    memberInfo: visibleMembers.map((member) => ({
                        ...member,
                        classSeq: item.classInfo.classSeq,
                    })),
                };
            });
            setClassMemberList(newList);
        }
    }, [sMyClassMemberList]);

    useEffect(() => {
        if (hours === 0 && minutes === 0 && purpose !== 'class') {
            setTimerWhether(false);
        }
    }, [hours, minutes, purpose]);

    const getVisibleTags = () =>
        tags.map((tag) => {
            const classSeq = Number(tag.claId.split('-')[4]);
            const classItem = classMemberList.find((item) => item.classInfo.classSeq === classSeq);

            if (!classItem) return tag;

            const visibleMemberIds = new Set(
                classItem.memberInfo.map((member) => member.orgMemberId.split('M')[0])
            );

            return {
                ...tag,
                stdtUserInfo: tag.stdtUserInfo.filter((student) =>
                    visibleMemberIds.has(student.stdtUserId.split('-')[0])
                ),
            };
        });

    const handleClaIdVerification = async (claId: string) => {
        const res = await publishedBoxApi.getClaIdVerification({ claId: claId });
        if (!res?.claInfo) return true;
        else return false;
    };

    const onRegister = async () => {
        if (registerStudent?.isPending || savePublish?.isPending) return;
        const _curriBook = checkCurriBookList && checkCurriBookList[0];
        const claId = 'viva-' + sDefaultUserId + '-' + timestamp + '-' + _curriBook;
        const visibleTags = getVisibleTags();

        if (purpose === 'URL' && !(await handleClaIdVerification(claId))) {
            return showToast('클래스 ID 인증 실패 다시 시도해주세요.');
        }
        registerStudent.mutate(
            {
                tcUserId: sUserId,
                claList:
                    purpose === 'class'
                        ? visibleTags //NOTE: claNm: 현재 클래스 멤버 리스트 데이터의 clsName 사용중 (변경이 가능한 값), 비바런 쪽에서는 변경이 불가능한 schClassName 값을 사용해서 표기중 논의 결과 clsName 데이터 사용
                        : [
                              {
                                  claId: claId,
                                  schlCd: '',
                                  schlNm: '',
                                  year: '',
                                  claNm: '',
                                  gradeCd: '',
                                  stdtUserInfo: [],
                              },
                          ],
            },
            {
                onSuccess: (res) => {
                    sendTraceLog(
                        '',
                        '',
                        '',
                        'debug',
                        '[SBANK_LOG]registerStudent purpose = ',
                        purpose,
                        ', res = ',
                        res
                    );
                    if (res?.resultOk) {
                        onPublish();
                    } else {
                        if (res?.resultMsg === 'claId 와 stdtId 시퀀스가 일치하지않습니다') {
                            showToast('잠시 후 다시 시도해 주세요.', 2000, 'error');
                        } else {
                            showToast('회원 정보 등록 실패', 2000, 'error');
                        }
                    }
                },
                onError: () =>
                    showModal({
                        isCustom: true,
                        id: 'error',
                        initContents: () => <ErrorPopup />,
                    }),
            }
        );
    };

    const onPublish = () => {
        if (savePublish?.isPending) return;
        const visibleTags = getVisibleTags();
        savePublish.mutate(
            {
                examIdList: checkIdList!,
                tcUserId: sUserId,
                publishType: purposeToNum(),
                // 응시 기간
                pdEvlStDt: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
                pdEvlEdDt: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
                pdSetAt:
                    (purpose !== 'class' && startDate && endDate) ||
                    (purpose === 'class' && startDate && endDate && periodWhether)
                        ? 'Y'
                        : 'N',
                // 제한 시간
                timStAt: timerWhether && purpose !== 'output' ? 'Y' : 'N',
                timTime: timerWhether
                    ? moment().hours(hours).minute(minutes).format('HH:mm:00')
                    : '',
                podOptionList:
                    purpose === 'output'
                        ? [
                              { code: 'pod_answer', val: 'Y' },
                              { code: 'pod_description', val: 'Y' },
                              { code: 'pod_eng_description', val: 'N' },
                              { code: 'pod_letter', val: 'pod_letter_A4' },
                              { code: 'pod_section', val: 'pod_section_1' },
                              { code: 'pod_template', val: 'pod_template_01' },
                              { code: 'pod_qr', val: 'pod_qr_last' },
                          ]
                        : undefined,
                claList:
                    purpose === 'class'
                        ? visibleTags.map((e) => ({
                              claId: e.claId,
                              stdtUserId: e.stdtUserInfo.map((s) => s.stdtUserId),
                          }))
                        : [
                              {
                                  claId:
                                      'viva-' +
                                      sDefaultUserId +
                                      '-' +
                                      timestamp +
                                      '-' +
                                      (checkCurriBookList && checkCurriBookList[0]),
                                  stdtUserId: [],
                              },
                          ],
            },
            {
                onSuccess: (res) => {
                    sendTraceLog(
                        '',
                        '',
                        '',
                        'debug',
                        '[SBANK_LOG] savePublish purpose = ',
                        purpose,
                        ', res = ',
                        res
                    );
                    sendTraceLog(
                        '',
                        '',
                        '',
                        'debug',
                        '[SBANK_LOG]savePublish startDate =',
                        startDate,
                        ', endDate = ',
                        endDate
                    );
                    sendTraceLog(
                        '',
                        '',
                        '',
                        'debug',
                        '[SBANK_LOG]savePublish timerWhether =',
                        timerWhether,
                        ', claList = ',
                        tags
                    );
                    if (res?.resultOk) {
                        onClose();
                        if (purpose === 'output') {
                            setPodV2PublishGroupCache(
                                res.publishGrpId,
                                checkList?.map((item) => ({
                                    setsId: item.setsId,
                                    title: item.name,
                                })) ?? []
                            );
                            moveToPodV2({ publishGrpId: res.publishGrpId });
                            return;
                        }
                        if (purpose === 'class') {
                            vivaClassEvalSave({
                                publishGrpId: res.publishGrpId,
                                type: 'normal',
                                studentList: visibleTags,
                            });
                        }
                        showModal({
                            id: 'publish-complete',
                            title: '발행 완료',
                            initContents: () => (
                                <PublishedCompletePopup
                                    publishGrpId={res.publishGrpId}
                                    timerWhether={timerWhether}
                                    hours={hours}
                                    minutes={minutes}
                                />
                            ),
                        });
                    } else showToast('발행 실패', 2000, 'error');
                },
                onError: () =>
                    showModal({
                        isCustom: true,
                        id: 'error',
                        initContents: () => <ErrorPopup />,
                    }),
            }
        );
    };

    const purposeToNum = () => {
        switch (purpose) {
            case 'class':
                return 1;
            case 'URL':
                return 2;
            case 'output':
                return 3;
            default:
                return 1;
        }
    };

    const onClear = (type?: 'clear' | 'tab') => {
        if (type !== 'tab') {
            setPurpose(undefined);
        }
        setTags([]);
        setStartDate(undefined);
        setEndDate(undefined);
        setHours(0);
        setMinutes(0);
        setTimerWhether(false);
        setPeriodWhether(false);
    };

    const onRecentOption = (rPurpose: DATA.PurposeType) => {
        setPurpose(rPurpose);
    };

    return (
        <div className="pop_wrap publish_option publish_type vivaClass">
            <Modal
                open={recentPublishedVisible}
                onClose={setRecentPublishedVisible}
                noButton={true}
                title="최근 발행 옵션"
                containerClass="pop_wrap publish_option lately_publish_choice"
            >
                <RecentPublishedOption
                    onRecentOption={onRecentOption}
                    onClose={setRecentPublishedVisible}
                />
            </Modal>
            <div className="modal_header">
                <div className="title">발행 옵션 설정</div>
            </div>
            <div className="pop_contents publish_option_popcont">
                <PublishOptionHeader
                    step1_disabled_true={step1_disabled_true}
                    step2_disabled_true={step2_disabled_true}
                    step2_disabled_true_class={step2_disabled_true_class}
                    step3_disabled_true_class={step3_disabled_true_class}
                    purpose={purpose}
                />
                {/* step1 */}
                <PublishOptionStep1
                    checkNameList={checkNameList}
                    onClear={onClear}
                    purpose={purpose}
                    setPurpose={setPurpose}
                />
                {purpose === 'class' && step1_disabled_true && (
                    <PublishOptionStep2Class
                        selectClass={selectClass}
                        setSelectClass={setSelectClass}
                        classMemberList={classMemberList}
                        setClassMemberList={setClassMemberList}
                        checkCurriBookList={checkCurriBookList}
                        tags={tags}
                        setTags={setTags}
                        onClose={onClose}
                        checkList={checkList}
                    />
                )}
                {/* step2 / step3 */}
                {((step1_disabled_true && purpose !== 'class') || step2_disabled_true_class) &&
                    purpose !== 'output' && (
                        <PublishOptionPeriod
                            purpose={purpose}
                            startDate={startDate}
                            endDate={endDate}
                            hours={hours}
                            minutes={minutes}
                            periodWhether={periodWhether}
                            timerWhether={timerWhether}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            setHours={setHours}
                            setMinutes={setMinutes}
                            setPeriodWhether={setPeriodWhether}
                            setTimerWhether={setTimerWhether}
                        />
                    )}

                {/* 버튼 */}
                <div className="btnwrap right">
                    <button
                        type="button"
                        className="btn btn-icon2 reload2"
                        onClick={() => onClear()}
                    >
                        초기화
                    </button>
                </div>

                <div className="bt_wrap cont-mt2">
                    <Dialog.Close asChild>
                        <button className="btn btn-default line_gray1 pop_cancle">취소</button>
                    </Dialog.Close>
                    <button
                        className="btn btn-default blue pop_submit"
                        disabled={publishBtnDisabled}
                        onClick={onRegister}
                    >
                        {purpose === 'output' ? '출력 옵션 설정' : '발행하기'}
                    </button>
                </div>
            </div>
            <Dialog.Close asChild>
                <button type="button" className="btn-x btn-x-white">
                    <span className="sr-only">팝업 닫기</span>
                </button>
            </Dialog.Close>
        </div>
    );
};
